import React, { useMemo, useState } from "react";
import { RadarPerspective } from "@/components/charts/RadarPerspective";
import { ScoreRing } from "@/components/charts/ScoreRing";
import { WeightedBars } from "@/components/charts/WeightedBars";
import { VERTICALS } from "@/lib/perspective";
import { Sparkles, Users, AlertCircle } from "lucide-react";

const TABS = { living: "Living", invests: "Investment", luxe: "Luxe" };

export const PropertyScorecard = ({ property }) => {
  const cats = property.category || [];
  const [active, setActive] = useState(cats[0] || "living");

  const vertical = VERTICALS[active];
  const radarData = useMemo(
    () => vertical.criteria.map((c) => ({ criterion: c.label, value: property.scores?.[c.key] ?? 60 })),
    [property, vertical]
  );
  const bars = useMemo(
    () => vertical.criteria.map((c) => ({ key: c.key, label: c.label, value: property.scores?.[c.key] ?? 60 })),
    [property, vertical]
  );
  const index = Math.round(radarData.reduce((s, d) => s + d.value, 0) / radarData.length);

  return (
    <div data-testid="property-scorecard">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="overline">The Archedia Perspective</div>
          <h2 className="mt-2 font-serif text-3xl font-light text-[var(--text-primary)] sm:text-4xl">How to view this property</h2>
        </div>
        {cats.length > 1 && (
          <div className="inline-flex rounded-full border border-[var(--border-subtle)] p-1">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`scorecard-tab-${c}`}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition ${active === c ? "bg-[var(--accent-gold)] text-[#071711]" : "text-[var(--text-muted)] hover:text-[var(--accent-gold)]"}`}
              >
                {TABS[c]}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_1fr]">
        <div className="arch-card p-6">
          <div className="mb-2 text-sm font-medium text-[var(--text-primary)]">{vertical.perspectiveLabel} — radar</div>
          <RadarPerspective data={radarData} />
        </div>
        <div className="arch-card flex flex-col justify-between p-6">
          <div className="flex items-center gap-6">
            <ScoreRing value={index} sublabel="Index" size={128} />
            <div>
              <div className="overline">Perspective Index</div>
              <p className="mt-2 text-sm text-[var(--text-muted)]">
                An illustrative Archedia evaluation across {vertical.criteria.length} dimensions — a way to see the property, not a market guarantee.
              </p>
            </div>
          </div>
          <div className="mt-6">
            <WeightedBars items={bars.slice(0, 5)} />
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        <div className="arch-card p-6">
          <Sparkles className="h-5 w-5 text-[var(--accent-gold)]" />
          <h4 className="mt-3 font-serif text-xl text-[var(--text-primary)]">Why It Stands Out</h4>
          <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">{property.whyStandout}</p>
        </div>
        <div className="arch-card p-6">
          <Users className="h-5 w-5 text-[var(--accent-gold)]" />
          <h4 className="mt-3 font-serif text-xl text-[var(--text-primary)]">Who It May Suit</h4>
          <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">{property.whoSuits}</p>
        </div>
        <div className="arch-card p-6">
          <AlertCircle className="h-5 w-5 text-[var(--accent-gold)]" />
          <h4 className="mt-3 font-serif text-xl text-[var(--text-primary)]">What to Consider</h4>
          <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">{property.whatConsider}</p>
        </div>
      </div>
    </div>
  );
};

export default PropertyScorecard;
